/**
 * BrandStylesPanel
 *
 * Global brand overrides for a page: primary color, font family, border radius.
 * Values are saved to `blockSettings.brandStyles` and applied via CSS variable
 * overrides on the template wrapper in SiteTemplateRenderer.
 */
import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { X } from 'lucide-react';

/* ─── Types ──────────────────────────────────────────────────────── */

export interface BrandStyles {
  primaryHsl?: string;    // e.g. "0 84% 52%"
  fontFamily?: string;    // key: "inter" | "playfair" | "georgia" | "mono" | "raleway"
  borderRadius?: string;  // rem string: "0" | "0.25" | "0.5" | "1" | "9999"
}

export interface BrandStylesPanelProps {
  isOpen: boolean;
  value: BrandStyles;
  onClose: () => void;
  onChange: (styles: BrandStyles) => void;
}

/* ─── Presets ─────────────────────────────────────────────────────── */

const COLOR_PRESETS = [
  { label: 'Red',    hsl: '0 84% 52%',    bg: '#e53e3e' },
  { label: 'Yellow', hsl: '56 95% 56%',   bg: '#f9ef23' },
  { label: 'Blue',   hsl: '221 83% 53%',  bg: '#3b82f6' },
  { label: 'Green',  hsl: '142 76% 36%',  bg: '#16a34a' },
  { label: 'Purple', hsl: '262 83% 58%',  bg: '#9333ea' },
  { label: 'Orange', hsl: '25 95% 53%',   bg: '#f97316' },
  { label: 'Teal',   hsl: '174 80% 35%',  bg: '#0d9488' },
  { label: 'Slate',  hsl: '220 9% 25%',   bg: '#334155' },
];

const FONT_OPTIONS = [
  { key: 'inter',     label: 'Inter',          style: 'font-sans' },
  { key: 'playfair',  label: 'Playfair Display', style: 'font-serif' },
  { key: 'georgia',   label: 'Georgia',         style: 'font-serif' },
  { key: 'raleway',   label: 'Raleway',          style: 'font-sans' },
  { key: 'mono',      label: 'Monospace',        style: 'font-mono' },
];

const RADIUS_OPTIONS = [
  { key: '0',    label: 'None',   preview: 'rounded-none' },
  { key: '0.25', label: 'Slight', preview: 'rounded-sm' },
  { key: '0.5',  label: 'Medium', preview: 'rounded-md' },
  { key: '1',    label: 'Large',  preview: 'rounded-xl' },
  { key: '9999', label: 'Full',   preview: 'rounded-full' },
];

/* ─── Component ──────────────────────────────────────────────────── */

export default function BrandStylesPanel({
  isOpen,
  value,
  onClose,
  onChange,
}: BrandStylesPanelProps) {
  const set = (partial: Partial<BrandStyles>) => onChange({ ...value, ...partial });

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        side="right"
        className="w-[320px] sm:w-[340px] flex flex-col p-0 gap-0 overflow-hidden"
      >
        <SheetHeader className="px-5 py-4 border-b border-border shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-base font-semibold">Brand Styles</SheetTitle>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Override brand colors, fonts, and shape for this page.
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">

          {/* ── Primary Color ─── */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Primary Color
            </p>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {COLOR_PRESETS.map(preset => (
                <button
                  key={preset.hsl}
                  title={preset.label}
                  onClick={() => set({ primaryHsl: preset.hsl })}
                  className={`h-8 w-full rounded-md border-2 transition-all ${
                    value.primaryHsl === preset.hsl
                      ? 'border-foreground scale-105 shadow-md'
                      : 'border-transparent hover:border-foreground/30'
                  }`}
                  style={{ backgroundColor: preset.bg }}
                />
              ))}
            </div>
            <div className="space-y-1">
              <label className="text-[11px] text-muted-foreground">Custom HSL (h s% l%)</label>
              <input
                className="w-full px-3 py-1.5 text-xs border border-border rounded-md bg-background font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="e.g. 210 80% 50%"
                value={value.primaryHsl ?? ''}
                onChange={e => set({ primaryHsl: e.target.value || undefined })}
              />
            </div>
            {value.primaryHsl && (
              <div
                className="mt-2 h-8 rounded-md border border-border"
                style={{ backgroundColor: `hsl(${value.primaryHsl})` }}
              />
            )}
          </div>

          {/* ── Font Family ─── */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Font Family
            </p>
            <div className="space-y-1.5">
              {FONT_OPTIONS.map(font => (
                <button
                  key={font.key}
                  onClick={() => set({ fontFamily: value.fontFamily === font.key ? undefined : font.key })}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md border text-sm transition-all ${
                    value.fontFamily === font.key
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border hover:border-primary/50 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <span className={font.style}>{font.label}</span>
                  {value.fontFamily === font.key && (
                    <span className="text-[10px] text-primary font-medium">Active</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* ── Border Radius ─── */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Border Radius
            </p>
            <div className="grid grid-cols-5 gap-1.5">
              {RADIUS_OPTIONS.map(r => (
                <button
                  key={r.key}
                  onClick={() => set({ borderRadius: value.borderRadius === r.key ? undefined : r.key })}
                  className={`flex flex-col items-center gap-1.5 p-2 border rounded-md transition-all ${
                    value.borderRadius === r.key
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div
                    className={`w-7 h-7 border-2 ${
                      value.borderRadius === r.key ? 'border-primary bg-primary/20' : 'border-muted-foreground/40 bg-muted'
                    } ${r.preview}`}
                  />
                  <span className="text-[9px] text-muted-foreground leading-tight text-center">{r.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Reset ─── */}
          {(value.primaryHsl || value.fontFamily || value.borderRadius) && (
            <button
              onClick={() => onChange({})}
              className="w-full text-xs text-muted-foreground hover:text-destructive transition-colors py-1.5 border border-dashed border-border rounded-md hover:border-destructive/50"
            >
              Reset to tenant defaults
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
